/**
 * Embedded threshold bundle creation form.
 */

import type { Source } from '@highimpact-frontend/api-types';
import {
  Alert,
  Button,
  CircularProgress,
  Divider,
  Snackbar,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import { ThresholdBundleFormContent } from './threshold-bundle-form/ThresholdBundleFormContent';
import {
  type CoordinateSystem,
  type RepresentationMode,
  type ThresholdDefinitionCreate,
  type ThresholdDefinitionPatchPayload,
  type ThresholdDefinitionPayload,
  type ThresholdFormMode,
  type ThresholdSubmitPayload,
  useThresholdBundleFormController,
} from './threshold-bundle-form/threshold-bundle-form.hook';

// -- type definitions --

export interface ThresholdBundleCreateFormBaseProps {
  readonly availableSources?: Record<string, Source[]>;
  readonly allowedCoordinateSystems?: CoordinateSystem[];
  readonly allowedRepresentationModes?: RepresentationMode[];
  readonly submitToApi?: boolean;
  readonly title?: string;
  readonly onCancel?: () => void;
}

export interface ThresholdBundleCreateFormCreateProps
  extends ThresholdBundleCreateFormBaseProps {
  readonly mode?: 'create';
  readonly thresholdId?: never;
  readonly initialDefinition?: never;
  readonly onCreated?: (payload: ThresholdDefinitionPayload) => void;
  readonly onUpdated?: never;
}

export interface ThresholdBundleCreateFormEditProps
  extends ThresholdBundleCreateFormBaseProps {
  readonly mode: 'edit-definition';
  readonly thresholdId: number;
  readonly initialDefinition?: Partial<ThresholdDefinitionCreate>;
  readonly onCreated?: never;
  readonly onUpdated?: (payload: ThresholdDefinitionPatchPayload) => void;
}

export type ThresholdBundleCreateFormProps =
  | ThresholdBundleCreateFormCreateProps
  | ThresholdBundleCreateFormEditProps;

// -- constants --

const STEPS = ['Définition', 'Assignation', 'Style', 'Confirmation'] as const;
const EDIT_STEPS = ['Définition'] as const;

const isThresholdDefinitionPayload = (
  payload: ThresholdSubmitPayload
): payload is ThresholdDefinitionPayload => {
  return (
    'definition' in payload &&
    'assignments' in payload &&
    'default_style' in payload
  );
};

const isThresholdDefinitionPatchPayload = (
  payload: ThresholdSubmitPayload
): payload is ThresholdDefinitionPatchPayload => {
  return !isThresholdDefinitionPayload(payload);
};

// -- main function --

export function ThresholdBundleCreateForm(
  props: ThresholdBundleCreateFormProps
) {
  const {
    availableSources,
    allowedCoordinateSystems,
    allowedRepresentationModes,
    submitToApi = true,
    title = 'Créer un seuil',
    onCancel,
  } = props;
  const mode: ThresholdFormMode =
    props.mode === 'edit-definition' ? 'edit-definition' : 'create';
  const onCreated =
    props.mode === 'edit-definition' ? undefined : props.onCreated;
  const onUpdated =
    props.mode === 'edit-definition' ? props.onUpdated : undefined;

  const formRef = useRef<HTMLFormElement | null>(null);
  const hasSingleSourceGroup = useMemo(
    () => Object.keys(availableSources ?? {}).length === 1,
    [availableSources]
  );
  const singleSourceGroupKey = useMemo(
    () => Object.keys(availableSources ?? {})[0] ?? null,
    [availableSources]
  );

  const controller = useThresholdBundleFormController(
    props.mode === 'edit-definition'
      ? {
          availableSources,
          allowedCoordinateSystems,
          allowedRepresentationModes,
          submitToApi,
          mode: 'edit-definition',
          thresholdId: props.thresholdId,
          initialDefinition: props.initialDefinition,
        }
      : {
          availableSources,
          allowedCoordinateSystems,
          allowedRepresentationModes,
          submitToApi,
          mode: 'create',
        }
  );

  const steps = mode === 'edit-definition' ? EDIT_STEPS : STEPS;
  const submitButtonLabel =
    mode === 'edit-definition'
      ? controller.isSubmitting
        ? 'Mise a jour en cours...'
        : 'Mettre a jour le seuil'
      : controller.isSubmitting
      ? 'Création en cours...'
      : 'Créer le seuil';

  useEffect(() => {
    controller.openDialog();
    // Open once on mount to initialize local state for embedded rendering.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      hasSingleSourceGroup &&
      singleSourceGroupKey &&
      controller.selectedSourceGroupKeys.length === 0
    ) {
      controller.setSelectedSourceGroupKeys([singleSourceGroupKey]);
    }
  }, [
    controller,
    controller.selectedSourceGroupKeys,
    hasSingleSourceGroup,
    singleSourceGroupKey,
  ]);

  useEffect(() => {
    if (!controller.lastPayload) {
      return;
    }

    if (mode === 'edit-definition') {
      if (isThresholdDefinitionPatchPayload(controller.lastPayload)) {
        onUpdated?.(controller.lastPayload);
      }
      return;
    }

    if (mode === 'create') {
      if (isThresholdDefinitionPayload(controller.lastPayload)) {
        onCreated?.(controller.lastPayload);
      }
    }
  }, [controller.lastPayload, mode, onCreated, onUpdated]);

  const handleProgressAction = () => {
    if (controller.activeStep < steps.length - 1) {
      if (
        controller.activeStep === 2 &&
        !controller.validateForConfirmation()
      ) {
        return;
      }
      controller.setActiveStep(controller.activeStep + 1);
      return;
    }

    formRef.current?.requestSubmit();
  };

  const handleCancel = () => {
    onCancel?.();
  };

  return (
    <Stack spacing={2.5}>
      <Typography variant="h6" fontWeight={700}>
        {title}
      </Typography>

      <Stepper activeStep={controller.activeStep} alternativeLabel>
        {steps.map((label, index) => (
          <Step key={label} completed={index < controller.activeStep}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {controller.submitError ? (
        <Alert severity="error">{controller.submitError}</Alert>
      ) : null}

      <form
        ref={formRef}
        onSubmit={(e) => {
          e.stopPropagation();
          void controller.handleSubmit(e);
        }}
        noValidate
      >
        <Stack spacing={2.5}>
          <ThresholdBundleFormContent controller={controller} />

          <Divider />

          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              color="inherit"
              disabled={controller.isSubmitting}
              onClick={handleCancel}
            >
              Annuler
            </Button>
            {controller.activeStep > 0 ? (
              <Button
                variant="outlined"
                disabled={controller.isSubmitting}
                type="button"
                onClick={() =>
                  controller.setActiveStep(controller.activeStep - 1)
                }
              >
                Précédent
              </Button>
            ) : null}
            <Button
              variant="contained"
              type="button"
              disabled={controller.isSubmitting}
              onClick={handleProgressAction}
              startIcon={
                controller.activeStep === steps.length - 1 &&
                controller.isSubmitting ? (
                  <CircularProgress color="inherit" size={16} />
                ) : undefined
              }
            >
              {controller.activeStep < steps.length - 1
                ? 'Suivant'
                : submitButtonLabel}
            </Button>
          </Stack>
        </Stack>
      </form>

      <Snackbar
        open={controller.snackbar.open}
        autoHideDuration={4200}
        onClose={controller.closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={controller.snackbar.severity}
          onClose={controller.closeSnackbar}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {controller.snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
}

export default ThresholdBundleCreateForm;
